import { supabase } from './supabaseClient';
import questions from './pages/data/questions';

// Returns full question objects the user has missed and not yet mastered.
// "Mastered" means the most recent attempt for that qid was correct.
export async function getMissedQuestions(userId, activeLicence) {
  try {
    const { data, error } = await supabase
      .from('question_answers')
      .select('qid, correct, answered_at')
      .eq('user_id', userId)
      .eq('licence', activeLicence)
      .order('answered_at', { ascending: false });

    if (error) throw error;
    if (!data || data.length === 0) return [];

    // Rows are DESC by answered_at, so first occurrence per qid = latest attempt.
    const latestByQid = new Map();
    for (const row of data) {
      if (!latestByQid.has(row.qid)) {
        latestByQid.set(row.qid, row.correct);
      }
    }

    const missedQids = new Set(
      [...latestByQid.entries()]
        .filter(([, correct]) => !correct)
        .map(([qid]) => qid)
    );

    if (missedQids.size === 0) return [];

    return questions.filter(
      (q) => missedQids.has(q.qid) && q.licences.includes(activeLicence)
    );
  } catch (err) {
    console.error('[quizStats] getMissedQuestions error:', err);
    return [];
  }
}

// Returns the topic where the user's accuracy is lowest, if any topic is
// below 70% with at least 5 answered questions. Returns null if none qualifies.
export async function getWeakestTopic(userId, activeLicence) {
  try {
    const { data, error } = await supabase
      .from('question_answers')
      .select('topic, correct')
      .eq('user_id', userId)
      .eq('licence', activeLicence);

    if (error) throw error;
    if (!data || data.length === 0) return null;

    const topicStats = new Map();
    for (const row of data) {
      const s = topicStats.get(row.topic) || { total: 0, correct: 0 };
      s.total += 1;
      if (row.correct) s.correct += 1;
      topicStats.set(row.topic, s);
    }

    let weakest = null;

    for (const [topic, s] of topicStats.entries()) {
      if (s.total < 5) continue;
      const accuracy = (s.correct / s.total) * 100;
      if (accuracy >= 70) continue;

      if (!weakest || accuracy < weakest.accuracy) {
        const sample = questions.find(
          (q) => q.topic === topic && q.licences.includes(activeLicence)
        );
        weakest = {
          topic,
          topicLabel: sample?.topicLabel ?? topic,
          accuracy: Math.round(accuracy),
          totalAnswered: s.total,
          correctCount: s.correct,
        };
      }
    }

    return weakest;
  } catch (err) {
    console.error('[quizStats] getWeakestTopic error:', err);
    return null;
  }
}
